/**
 * The empty-state art (CAE-1540) — the big line-art icons the three empty
 * flavours are drawn around, ported path-for-path from the handoff's screen
 * apps (`contests-list-app.jsx`, `fixtures-app.jsx`, `reels-app.jsx`,
 * `prizes-app.jsx`, `catalogue-app.jsx`).
 *
 * Separate from `line-icons.tsx` on purpose: that file is the rail's
 * destinations and the 13px glyphs the primitives draw *with*, all on a
 * 24-unit grid. These are art — rendered at 56px inside an 88px tile, or 36px
 * inside a 56px one — and five of the six are drawn on the handoff's own 56
 * grid rather than a 24.
 *
 * That mixed grid is the design's, not a slip to normalise. `NoResultsIcon` is
 * a 24-grid glyph the handoff scales up to 36, so its 1.6 stroke reads heavier
 * than the 1.5 the 56-grid icons carry at 56 — which is what design signed off,
 * and the same reason `line-icons.tsx` keeps its own per-icon stroke widths.
 *
 * They stay MUI `SvgIcon`s so a caller sizes them through `fontSize`, exactly
 * as the rail sizes its icons. `AdminEmpty` asks for 56, `AdminEmptyInline` and
 * `AdminEmptySection` for 36 — the icon itself is size-agnostic.
 */
import SvgIcon from '@mui/material/SvgIcon';
import type { SvgIconProps } from '@mui/material/SvgIcon';

/**
 * Two MUI defaults have to be undone here, and both are silent when they bite.
 *
 * `MuiSvgIcon-root` sets `fill: currentColor`, which would flood these outline
 * shapes solid, and a `fill` attribute cannot beat that class — so the override
 * has to come through `sx`. Same reason `line-icons.tsx` has its own wrapper;
 * this one takes the viewBox per icon rather than fixing it at 24.
 *
 * And `fontSize="inherit"`, which is what actually makes these size with their
 * container. `SvgIcon`'s default is `medium`, a class pinning it to 1.5rem —
 * and a class beats the `font-size` the tile sets, so the art rendered at 24px
 * inside an 88px tile while every test that sized an icon directly still
 * passed. The rail's icons escape this because they are sized through their own
 * `sx`; these are sized by the box they sit in, so they must inherit.
 */
const EmptyArtIcon = ({ sx, ...props }: SvgIconProps) => (
  <SvgIcon
    fontSize="inherit"
    {...props}
    sx={[{ fill: 'none' }, ...(Array.isArray(sx) ? sx : [sx])]}
  />
);

/**
 * The filtered empty's magnifier. The one icon here on the 24 grid rather than
 * 56 — the handoff draws it that way and scales it to 36, so its stroke reads
 * heavier than the 56-grid art does at 56. That is design's own weighting.
 */
export const NoResultsIcon = (props: SvgIconProps) => (
  <EmptyArtIcon viewBox="0 0 24 24" {...props}>
    <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M16 16l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </EmptyArtIcon>
);

/** Contests list, all-empty. A ruled rectangle — a contest that isn't there. */
export const NoContestsIcon = (props: SvgIconProps) => (
  <EmptyArtIcon viewBox="0 0 56 56" {...props}>
    <rect x="10" y="10" width="36" height="36" rx="6" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M18 22h20M18 28h14M18 34h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      opacity=".5"
    />
  </EmptyArtIcon>
);

/**
 * Edit › Fixtures, nothing attached. Two crests with a dashed axis between
 * them — a fixture that has no two sides yet. The dimmed left circle is the
 * handoff's own: the pair reads as one absent match, not two.
 */
export const NoFixturesIcon = (props: SvgIconProps) => (
  <EmptyArtIcon viewBox="0 0 56 56" {...props}>
    <circle cx="20" cy="30" r="10" stroke="currentColor" strokeWidth="1.5" opacity=".35" />
    <circle cx="36" cy="30" r="10" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M28 22v16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeDasharray="2 3"
    />
  </EmptyArtIcon>
);

/** Edit › Reels, none built. Three reels with the middle one still dashed open. */
export const NoReelsIcon = (props: SvgIconProps) => (
  <EmptyArtIcon viewBox="0 0 56 56" {...props}>
    <rect
      x="7"
      y="14"
      width="12"
      height="28"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.5"
      opacity=".35"
    />
    <rect x="22" y="14" width="12" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <rect
      x="37"
      y="14"
      width="12"
      height="28"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.5"
      opacity=".35"
    />
    <path
      d="M28 22v12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeDasharray="2 3"
    />
  </EmptyArtIcon>
);

/** Edit › Prizes, no tiers. A single outlined star — what there is to win. */
export const NoPrizeTiersIcon = (props: SvgIconProps) => (
  <EmptyArtIcon viewBox="0 0 56 56" {...props}>
    <path
      d="M28 8l5 11 12 1.5-9 8 2 12-10-6-10 6 2-12-9-8 12-1.5z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </EmptyArtIcon>
);

/** Catalogue, upstream feed unreachable. A struck-through connection. */
export const FeedDisconnectedIcon = (props: SvgIconProps) => (
  <EmptyArtIcon viewBox="0 0 56 56" {...props}>
    <circle cx="28" cy="28" r="18" stroke="currentColor" strokeWidth="1.5" opacity=".35" />
    <path d="M14 14l28 28" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path
      d="M22 28c0-3.3 2.7-6 6-6M28 34c3.3 0 6-2.7 6-6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </EmptyArtIcon>
);
